import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { tasksApi, ApiError } from "@/api";
import type { TaskRow } from "@/types";
import { Pill } from "@/components/ui";
import { taskStatusTone, deadlineChip, humanise } from "@/lib/jobs";
import { useAuth } from "@/staff/auth/AuthContext";
import { TaskDrawer } from "./TaskDrawer";
import "./tasks-dashboard.css";

const PER_PAGE = 200;
const PRIORITY_RANK: Record<string, number> = { urgent: 4, high: 3, medium: 2, low: 1 };

type Quick = "all" | "overdue" | "today" | "week" | "no_date";
type Sort = "due_date" | "priority" | "created_at";

/**
 * Cross-job Tasks dashboard — one /list fetch of the live working set, then all
 * filtering/sorting/bucketing client-side (instant). Read + light-write:
 * complete, soft-delete, click through to the owning job. Full editing stays in
 * the job-detail task drawer. A history toggle refetches with done/cancelled.
 */
export function TasksDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id;

  const [drawer, setDrawer] = useState<TaskRow | "new" | null>(null);
  const [rows, setRows] = useState<TaskRow[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [history, setHistory] = useState(false);

  const [quick, setQuick] = useState<Quick>("all");
  const [mine, setMine] = useState(false);
  const [sort, setSort] = useState<Sort>("due_date");
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(
    (opts: { history?: boolean; initial?: boolean } = {}) => {
      let cancelled = false;
      if (opts.initial) setLoading(true);
      tasksApi
        .listTasks({
          per_page: PER_PAGE,
          sort_field: "due_date",
          sort_direction: "asc",
          include_done: opts.history,
          include_cancelled: opts.history,
        })
        .then((res) => {
          if (cancelled) return;
          setRows(res.items ?? []);
          setHasMore(!!res.paging?.has_more);
        })
        .catch((err) => {
          if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load tasks.");
        })
        .finally(() => {
          if (!cancelled && opts.initial) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    },
    [],
  );

  useEffect(() => load({ initial: true, history: false }), [load]);

  // The nav "New task" button routes here with { create: true } — open the
  // create drawer and strip the flag. Depends on `location` so it re-fires when
  // the button is clicked while already on this page (state-only navigation).
  useEffect(() => {
    if ((location.state as { create?: boolean } | null)?.create) {
      setDrawer("new");
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  function reload() {
    load({ history });
  }

  function toggleHistory() {
    const next = !history;
    setHistory(next);
    setLoading(true);
    load({ history: next, initial: true });
  }

  // Tiles reflect the true open set (the working fetch), independent of filters.
  const tiles = useMemo(() => {
    let overdue = 0, today = 0, week = 0, noDate = 0;
    for (const t of rows) {
      const d = t.days_remaining;
      if (d == null) noDate++;
      else if (d < 0) overdue++;
      else if (d === 0) today++;
      else if (d <= 7) week++;
    }
    return { overdue, today, week, noDate, total: rows.length };
  }, [rows]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let r = rows.filter((t) => {
      const d = t.days_remaining;
      if (quick === "overdue" && !(d != null && d < 0)) return false;
      if (quick === "today" && d !== 0) return false;
      if (quick === "week" && !(d != null && d >= 1 && d <= 7)) return false;
      if (quick === "no_date" && d != null) return false;
      if (mine && t.assigned_to_user_id !== userId) return false;
      if (term) {
        const hay = [t.title, t.job_name, t.job_number?.toString(), t.application_reference, t.notes, t.description]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
    const dueKey = (t: TaskRow) => (t.days_remaining == null ? Number.POSITIVE_INFINITY : t.days_remaining);
    r = [...r].sort((a, b) => {
      if (sort === "priority") {
        const diff = (PRIORITY_RANK[b.priority] ?? 0) - (PRIORITY_RANK[a.priority] ?? 0);
        return diff !== 0 ? diff : dueKey(a) - dueKey(b);
      }
      if (sort === "created_at") return b.created_at - a.created_at;
      return dueKey(a) - dueKey(b);
    });
    return r;
  }, [rows, quick, mine, q, sort, userId]);

  async function complete(t: TaskRow) {
    if (busyId != null) return;
    setBusyId(t.id);
    setRows((prev) => prev.map((r) => (r.id === t.id ? { ...r, status: "done" } : r))); // optimistic strike
    try {
      await tasksApi.updateTask({ id: t.id, status: "done" });
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not complete the task.");
      reload();
    } finally {
      setBusyId(null);
    }
  }

  const filtersActive = quick !== "all" || mine || q.trim() !== "";

  return (
    <section className="tdash">
      <header className="tdash__head">
        <h1>Tasks</h1>
        <p className="tdash__subtitle">
          {history
            ? "Showing done & cancelled tasks"
            : `${tiles.overdue} overdue · ${tiles.week} due this week · ${tiles.total} open`}
        </p>
      </header>

      {!history ? (
        <div className="tdash__tiles">
          <Tile label="Overdue" value={tiles.overdue} urgent={tiles.overdue > 0} active={quick === "overdue"} onClick={() => setQuick("overdue")} />
          <Tile label="Due today" value={tiles.today} active={quick === "today"} onClick={() => setQuick("today")} />
          <Tile label="Next 7 days" value={tiles.week} active={quick === "week"} onClick={() => setQuick("week")} />
          <Tile label="No due date" value={tiles.noDate} active={quick === "no_date"} onClick={() => setQuick("no_date")} />
          <Tile label="Open total" value={tiles.total} active={quick === "all"} onClick={() => setQuick("all")} />
        </div>
      ) : null}

      <div className="tdash__toolbar">
        <div className="tdash__pills">
          {([
            ["all", "All"],
            ["overdue", "Overdue"],
            ["today", "Due today"],
            ["week", "Next 7 days"],
            ["no_date", "No date"],
          ] as [Quick, string][]).map(([value, label]) => (
            <button key={value} type="button" className={`tdash-pill${quick === value ? " is-on" : ""}`} onClick={() => setQuick(value)}>
              {label}
            </button>
          ))}
        </div>

        <label className="tdash__mine" title={userId ? undefined : "Requires sign-in context"}>
          <input type="checkbox" checked={mine} disabled={!userId} onChange={(e) => setMine(e.target.checked)} />
          Mine
        </label>

        <select className="tdash__sort" value={sort} onChange={(e) => setSort(e.target.value as Sort)} aria-label="Sort tasks">
          <option value="due_date">Due date</option>
          <option value="priority">Priority</option>
          <option value="created_at">Recently added</option>
        </select>

        <input
          className="leads__search"
          type="search"
          placeholder="Search tasks, jobs, applications…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        <label className="tdash__history" title="Include done & cancelled (server refetch)">
          <input type="checkbox" checked={history} onChange={toggleHistory} />
          Show done &amp; cancelled
        </label>
      </div>

      {hasMore ? (
        <p className="tdash__banner" role="status">Showing the first {PER_PAGE} open tasks — use filters/search to narrow.</p>
      ) : null}

      {error ? (
        <p className="leads__error" role="alert">
          {error} <button type="button" className="tdash__retry" onClick={() => { setError(undefined); reload(); }}>Retry</button>
        </p>
      ) : null}

      {loading ? (
        <ul className="tdash-list">
          {Array.from({ length: 6 }).map((_, i) => <li key={i} className="tdash-row tdash-row--skel" aria-hidden="true" />)}
        </ul>
      ) : filtered.length === 0 ? (
        <p className="jobs-list__empty">
          {filtersActive ? (
            <>No tasks match this filter. <button type="button" className="tdash__retry" onClick={() => { setQuick("all"); setMine(false); setQ(""); }}>Clear filters</button></>
          ) : history ? (
            "No done or cancelled tasks."
          ) : (
            "Nothing on your plate. Tasks created on jobs show up here."
          )}
        </p>
      ) : (
        <ul className="tdash-list">
          {filtered.map((t) => (
            <TaskListRow
              key={t.id}
              task={t}
              busy={busyId === t.id}
              onEdit={() => setDrawer(t)}
              onComplete={() => complete(t)}
            />
          ))}
        </ul>
      )}

      {drawer ? (
        <TaskDrawer
          task={drawer === "new" ? null : drawer}
          onClose={() => setDrawer(null)}
          onSaved={() => { setDrawer(null); reload(); }}
        />
      ) : null}
    </section>
  );
}

function Tile({ label, value, urgent, active, onClick }: { label: string; value: number; urgent?: boolean; active?: boolean; onClick: () => void }) {
  return (
    <button type="button" className={`tdash-tile${active ? " is-on" : ""}${urgent ? " tdash-tile--urgent" : ""}`} onClick={onClick}>
      <span className="tdash-tile__value numeric">{value}</span>
      <span className="tdash-tile__label">{label}</span>
    </button>
  );
}

function TaskListRow({
  task,
  busy,
  onEdit,
  onComplete,
}: {
  task: TaskRow;
  busy: boolean;
  onEdit: () => void;
  onComplete: () => void;
}) {
  const due = task.days_remaining == null
    ? { label: "No due date", tone: "none" as const }
    : deadlineChip(task.days_remaining);
  const done = task.status === "done" || task.status === "cancelled";
  const meta = [
    task.job_id ? `#${task.job_number} · ${task.job_name || "Untitled job"}` : "No job linked",
    task.application_reference || null,
  ].filter(Boolean);

  return (
    <li className={`tdash-row${done ? " tdash-row--done" : ""}`} onClick={onEdit} title="Edit task">
      {!done ? (
        <button
          type="button"
          className="tdash-row__check"
          disabled={busy}
          aria-label="Mark complete"
          title="Mark complete"
          onClick={(e) => { e.stopPropagation(); onComplete(); }}
        />
      ) : (
        <span className="tdash-row__check tdash-row__check--done" aria-hidden="true">✓</span>
      )}

      <span className={`tdash-dot tdash-dot--${task.priority}`} title={`${humanise(task.priority)} priority`} aria-hidden="true" />

      <div className="tdash-row__main">
        <span className="tdash-row__title">{task.title}</span>
        <span className="tdash-row__meta">
          {meta.join(" · ")}
          {meta.length ? " · " : ""}
          <span className={due.tone === "overdue" ? "tdash-row__overdue" : undefined}>{due.label}</span>
        </span>
        {task.job_id ? (
          <Link
            to={`/staff/jobs/${task.job_id}`}
            state={{ tab: "tasks" }}
            className="tdash-row__openjob"
            onClick={(e) => e.stopPropagation()}
          >
            Open job ↗
          </Link>
        ) : null}
      </div>

      <Pill tone={taskStatusTone(task.status)}>{humanise(task.status)}</Pill>

      <span className="tdash-row__chevron" aria-hidden="true">›</span>
    </li>
  );
}
